import React from 'react'
import * as Yup from 'yup'

import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'

import { useParams } from 'react-router-dom'
import { Form } from 'react-final-form'
import { TextField, makeValidate } from 'mui-rff'

import { filterMaybe, useSearchParams } from '@edge/spark/utils'
import { UserSelectAutocomplete } from '../../../feature-dashboard-widgets'
import { useEditableDashboardsQuery, useCopyDashboardMutation } from '@edge/spark/io-dashboards'

interface FormDialogProps {
    open: boolean
    onClose: () => void
}

export const CopyDashboardDialog: React.FC<FormDialogProps> = ({ open, onClose }: FormDialogProps) => {
    const { uid: priorUserId } = useSearchParams()
    const { dashId: priorDashId } = useParams<{ dashId?: string }>()
    const { data, loading: queryLoading } = useEditableDashboardsQuery()
    const [copyDash, { loading: mutationLoading }] = useCopyDashboardMutation()

    const usersFromAdminQuery = data?.administrator?.users?.filter(filterMaybe) || []

    const schema = Yup.object({
        name: Yup.string().required(),
        userId: Yup.string().test(
            'userId',
            // eslint-disable-next-line no-template-curly-in-string
            '${path} is required',
            (val) => !!val || !usersFromAdminQuery.length,
        ),
    })
    const validate = makeValidate(schema)

    type Value = Yup.InferType<typeof schema>

    return (
        <Dialog open={open} onClose={onClose}>
            <Form<Value>
                validate={validate}
                onSubmit={(values) => {
                    onClose()
                    return copyDash({
                        variables: {
                            priorUserId,
                            priorDashId,
                            ...values,
                        },
                    })
                }}
                render={({ handleSubmit, invalid }): React.ReactNode => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle>Copy Dashboard</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Copy the dashboard to yourself
                                {usersFromAdminQuery.length ? ' or another user' : ''}.
                            </DialogContentText>
                            <TextField
                                label="New Dashboard Name"
                                required
                                name="name"
                                autoFocus
                                fullWidth
                                margin="normal"
                            />
                            {usersFromAdminQuery.length ? (
                                <UserSelectAutocomplete loading={queryLoading} users={usersFromAdminQuery} />
                            ) : undefined}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="primary">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={invalid || mutationLoading}
                                color="primary"
                                variant="contained"
                            >
                                Save
                            </Button>
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}
